import {useAuthService} from "~/composables/useAuthService";
import {useMessaging} from "~/composables/useMessaging";
import {computed} from "vue";
import {useRouter, useRoute} from "vue-router";
import {useMailer} from "~/composables/useMailer";
import {canSwitchClub} from "~/composables/useAdminRouteGuard";
import {signOutToHome} from "~/composables/useSignOut";
import {EVALS_ENABLED_STATE} from "~/composables/useAssistantEvals";

export function useNavigationItems() {
  const {
    isAuthenticated,
    user,
    logout,
    stopImpersonating,
    isImpersonating,
    isSuperUser
  } = useAuthService();

  const { unreadCount } = useMessaging();
  const {stringifyQuery} = useCrudConverters();
  const router = useRouter();
  const route = useRoute();

  const adminBreadcrumbs = useState('adminBreadcrumbs', () => []);
  // Filled once per session by the admin layout (useAssistantEvalsNav): true only when the
  // API's eval status says enabled, so the Evals item never points at a switched off console.
  const assistantEvalsEnabled = useState(EVALS_ENABLED_STATE, () => null);

  const convertToLocalRoute = (link) => {
    const config = useRuntimeConfig();
    const baseUrl = config.public.baseUrl;
    if(link.startsWith(baseUrl)) {
      return link.replace(baseUrl, '');
    }
    return null;
  }

  const setAdminBreadcrumbs = (items) => {
    adminBreadcrumbs.value = items;
  };

  const superUserItems = computed(() => {
    const items = [];
    items.push({
      icon: 'mdi-view-dashboard',
      title: 'Dashboard',
      link: `/admin/super`,
      active: false
    });
    items.push({
      icon: 'mdi-account',
      title: 'Users',
      link: `/admin/super/users`,
      active: route.path.includes('/admin/super/users')
    });
    items.push({
      icon: 'mdi-map-marker-path',
      title: 'Organiser journey',
      link: `/admin/super/journey`,
      active: route.path.includes('/admin/super/journey')
    });
    items.push({
      icon: 'mdi-email-edit-outline',
      title: 'Outreach',
      link: `/admin/super/outreach`,
      active: route.path.includes('/admin/super/outreach')
    });
    items.push({
      icon: 'mdi-access-point',
      title: 'Online users',
      link: `/admin/super/online-users`,
      active: route.path.includes('/admin/super/online-users')
    });
    items.push({
      icon: 'mdi-account-multiple',
      title: 'Players',
      link: `/admin/super/players`,
      active: route.path.includes('/admin/super/players')
    });
    items.push({
      icon: 'mdi-account-switch',
      title: 'Player Merges',
      link: `/admin/super/player-merges`,
      active: route.path.includes('/admin/super/player-merges')
    });
    items.push({
      icon: 'mdi-account-convert',
      title: 'Account Merges',
      link: `/admin/super/account-merges`,
      active: route.path.includes('/admin/super/account-merges')
    });
    items.push({
      icon: 'mdi-account-group',
      title: 'Clubs',
      link: `/admin/super/clubs`,
      active: route.path.includes('/admin/super/clubs')
    });
    items.push({
      icon: 'mdi-email-outline',
      title: 'Emails',
      appendIcon: "mdi-new-box",
      appendIconColor: "primary",
      link: `/admin/super/emails`,
      active: route.path.includes('/admin/super/emails')
    });
    items.push({
      icon: 'mdi-credit-card-outline',
      title: 'Payments',
      link: null,
      active: route.path.includes('/admin/super/payments')
          || route.path.includes('/admin/super/payment-accounts')
          || route.path.includes('/admin/super/commission')
          || route.path.includes('/admin/super/countries-commission')
          || route.path.includes('/admin/super/vat'),
      subItems: [
        {
          title: 'Overview',
          link: '/admin/super/payments',
          icon: 'mdi-cash-multiple'
        },
        {
          title: 'Connected accounts',
          link: '/admin/super/payment-accounts',
          icon: 'mdi-bank-outline'
        },
        {
          title: 'Countries and commission',
          link: '/admin/super/countries-commission',
          icon: 'mdi-percent-outline'
        },
        {
          title: 'VAT',
          link: '/admin/super/vat',
          icon: 'mdi-receipt-text-outline'
        }
      ]
    });
    // The platform Terms of Use and Privacy Policy. Distinct from Terms
    // Template below, which is the sample terms seeded into new paid meets.
    items.push({
      icon: 'mdi-scale-balance',
      title: 'Terms and privacy',
      link: `/admin/super/legal`,
      active: route.path.includes('/admin/super/legal')
    });
    items.push({
      icon: 'mdi-gavel',
      title: 'Terms Template',
      link: `/admin/super/terms-template`,
      active: route.path.includes('/admin/super/terms-template')
    });
    items.push({
      icon: 'mdi-help-box',
      title: 'Help',
      link: null,
      active: route.path.includes('/admin/super/help'),
      subItems: [
        {
          title: 'Categories',
          link: '/admin/super/help-categories',
          icon: 'mdi-folder-multiple'
        },
        {
          title: 'Articles',
          link: '/admin/super/help-articles',
          icon: 'mdi-help-circle'
        }
      ]
    });
    // The eval console sits under Assistant, and only where it is switched on.
    items.push(assistantEvalsEnabled.value === true
        ? {
          icon: 'mdi-robot-happy-outline',
          title: 'Assistant',
          link: null,
          active: route.path.includes('/admin/super/assistant'),
          subItems: [
            {title: 'Overview', link: '/admin/super/assistant', icon: 'mdi-chart-box-outline'},
            {title: 'Evals', link: '/admin/super/assistant/evals', icon: 'mdi-clipboard-check-outline'},
          ]
        }
        : {
          icon: 'mdi-robot-happy-outline',
          title: 'Assistant',
          link: `/admin/super/assistant`,
          active: route.path.includes('/admin/super/assistant')
        });
    items.push({
      icon: 'mdi-flag-outline',
      title: 'Message reports',
      link: `/admin/super/reports`,
      active: route.path.includes('/admin/super/reports')
    });
    items.push({
      icon: 'mdi-form-select',
      title: 'Prompts',
      link: `/admin/super/prompts`,
      active: route.path.includes('/admin/super/prompts')
    });
    items.push({
      icon: 'mdi-basketball',
      title: 'Sports',
      link: `/admin/super/sports`,
      active: route.path.includes('/admin/super/sports')
    });
    items.push({
      icon: 'mdi-link-variant',
      title: 'Short Links',
      link: `/admin/super/short-links`,
      active: route.path.includes('/admin/super/short-links')
    });
    items.push({
      icon: 'mdi-star-box',
      title: 'Testimonials',
      link: `/admin/super/testimonials`,
      active: route.path.includes('/admin/super/testimonials')
    });
    items.push({
      icon: 'mdi-send',
      title: 'Mass Mailer',
      link: `/admin/super/mailer`,
      active: route.path.includes('/admin/super/mailer')
    });
    items.push({
      icon: 'mdi-bell-ring',
      title: 'Push Notifications',
      link: `/admin/super/push`,
      active: route.path.includes('/admin/super/push')
    });
    items.push({
      icon: 'mdi-bullhorn',
      title: 'Announcements',
      link: `/admin/super/announcements`,
      active: route.path.includes('/admin/super/announcements')
    });
    items.push({
      icon: 'mdi-cellphone-arrow-down',
      title: 'Supported Versions',
      link: `/admin/super/supported-versions`,
      active: route.path.includes('/admin/super/supported-versions')
    });
    items.push({
      icon: 'mdi-star-outline',
      title: 'Review Prompt',
      link: `/admin/super/review-prompt`,
      active: route.path.includes('/admin/super/review-prompt')
    });
    items.push({
      icon: 'mdi-shield-check-outline',
      title: 'Two-step sign-in',
      link: `/admin/super/two-step`,
      active: route.path.includes('/admin/super/two-step')
    });
    return items;
  });
 const { filtersToQuery } = useMailer();
  const adminMenuItems = computed(() => {
    const items = [];
    if (isAuthenticated.value) {
      if (route.params.clubId
      ) {
        items.push({
          icon: 'mdi-account-group',
          title: 'Members',
          link: `/admin/clubs/${route.params.clubId}/members`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/members')
        });
        // Requests to follow players this club manages (no account of their own).
        items.push({
          icon: 'mdi-account-heart-outline',
          title: 'Follow requests',
          link: `/admin/clubs/${route.params.clubId}/follow-requests`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/follow-requests')
        });
        // Online Safety Act: reports members have made about this club's chats.
        items.push({
          icon: 'mdi-flag-outline',
          title: 'Message reports',
          link: `/admin/clubs/${route.params.clubId}/reports`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/reports')
        });
        // Every format lives under one group, alphabetically.
        // Medal for the GROUP; the trophy belongs to Tournaments alone.
        items.push({
          icon: 'mdi-medal-outline',
          title: 'Competitions',
          link: null,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/leagues')
              || route.path.includes('/admin/clubs/' + route.params.clubId + '/americanos')
              || route.path.includes('/admin/clubs/' + route.params.clubId + '/ladders')
              || route.path.includes('/admin/clubs/' + route.params.clubId + '/meets')
              || route.path.includes('/admin/clubs/' + route.params.clubId + '/tournaments'),
          subItems: [
            {
              title: 'Americano leagues',
              link: `/admin/clubs/${route.params.clubId}/americanos`,
              icon: 'mdi-account-switch'
            },
            {
              title: 'Box leagues',
              link: `/admin/clubs/${route.params.clubId}/leagues`,
              icon: 'mdi-table'
            },
            {
              title: 'Ladders',
              link: `/admin/clubs/${route.params.clubId}/ladders`,
              icon: 'mdi-ladder'
            },
            {
              title: 'Meets',
              link: `/admin/clubs/${route.params.clubId}/meets`,
              icon: 'mdi-handshake'
            },
            {
              title: 'Tournaments',
              link: `/admin/clubs/${route.params.clubId}/tournaments`,
              icon: 'mdi-trophy'
            }
          ]
        });
        items.push({
          icon: 'mdi-credit-card-outline',
          title: 'Payments',
          link: `/admin/clubs/${route.params.clubId}/payments`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/payments')
        });
        items.push({
          icon: 'mdi-scoreboard',
          title: 'Scoring Systems',
          link: `/admin/clubs/${route.params.clubId}/scoringSystems`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/scoringSystems')
        });
        items.push({
          icon: 'mdi-map-marker-outline',
          title: 'Playing Areas',
          link: `/admin/clubs/${route.params.clubId}/playingAreas`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/playingAreas')
        });
        items.push({
          icon: 'mdi-widgets-outline',
          title: 'Widgets',
          link: `/admin/clubs/${route.params.clubId}/widgets`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/widgets')
        });
        items.push({
          icon: 'mdi-television-play',
          title: 'Clubhouse TV',
          link: `/admin/clubs/${route.params.clubId}/displays`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/displays')
        });
        items.push({
          icon: 'mdi-bullhorn',
          title: 'Announcements',
          link: `/admin/clubs/${route.params.clubId}/announcements`,
          active: route.path.includes('/admin/clubs/' + route.params.clubId + '/announcements')
        });
        items.push({
          icon: 'mdi-send',
          title: 'Email Members',
          link: {
            ...route,
            query: {
              ...route.query,
              mailerQuery: filtersToQuery({
                clubIds: [route.params.clubId]
              })
            }
          },
          active: false
        });
      }

    }
    return items;
  });

  const adminLowerMenuItems = computed(() => {
    const items = [];
    if(!isSuperUser.value) {
      items.push({
        icon: 'mdi-help-circle-outline',
        title: 'Help',
        link: '/help'
      });
      items.push({
        icon: 'mdi-email-edit-outline',
        title: 'Contact Support',
        link: '/contact'
      });
    }
    // Every club admin: the page it opens is also where "Create a club" lives,
    // and a single-club admin has no other way to it. See canSwitchClub.
    if(isSuperUser.value || (isAuthenticated.value && canSwitchClub(user.value))) {
      items.push({
        title: 'Switch club',
        link: '/admin?switchClub=true',
        icon: 'mdi-swap-horizontal'
      });
    }
    items.push({
      title: 'Exit Admin',
      link: '/',
      icon: 'mdi-exit-to-app'
    });
    return items;
  })

  const mainMenuItems = computed(() => {
    const items = [];
    items.push(...[
      {title: 'Home', link: '/'},
      {title: 'Clubs', link: '/clubs'},
      {title: 'Sports', link: '/sports'},
      {title: 'Formats', link: '/formats'},
      {title: 'Features', link: '/features'}
    ]);
    items.push(...[
        {title: 'About', link: '/about'},
        {title: 'Help', link: '/help'},
        {title: 'Contact', link: '/contact'},
      ]);
    if (!isAuthenticated.value) {
      items.push({
        title: 'Sign in',
        link: '/login' + stringifyQuery({redirectUrl: route.path}, "?"),
        icon: 'mdi-lock'
      });
      items.push({title: 'Sign up', link: '/register', icon: 'mdi-pencil'});
    }
    return items;
  });
  const userTopRightItems = computed(() => {
    let items = [
      {icon: 'mdi-account', text: 'Your profile', route: 'YourProfile'},
    ];
    items.push({icon: 'mdi-logout', text: 'Sign out', action: 'logout'});
    if (isImpersonating.value) {
      items.push({icon: 'mdi-cancel', text: 'Stop impersonating', action: 'stopImpersonating'});
    }
    return items;
  });
  const onUserTopRightItemClick = async (item) => {
    if (item.link) {
      router.push(item.link).catch(() => {
      });
    } else if (item.action) {
      if (item.action === 'logout') {
        // One sign-out path for the whole site. Going home first no longer
        // works on its own: a required prompt holds that navigation at /prompt.
        await signOutToHome({router, logout});
      } else if (item.action === 'stopImpersonating') {
        await stopImpersonating();
      }
    } else if (item.route) {
      router.push({name: item.route}).catch(() => {
      });
    }
  };
  return {
    convertToLocalRoute,
    adminBreadcrumbs,
    setAdminBreadcrumbs,
    userTopRightItems,
    onUserTopRightItemClick,
    mainMenuItems,
    adminMenuItems,
    adminLowerMenuItems,
    superUserItems
  };
}

